import {
  useWSModalCustom,
  WSActions,
  WSControlGroup,
  WSForm,
  WSInputNumber,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import * as Yup from "yup";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { QUERY_PAYOUT_SETTINGS } from "../../../query/payments/keys";
import { usePayoutSettings } from "../../../query/payments/queries";
import { WSQueries } from "../../../query/WSQuery";
import { paymentsService } from "../../../services/payments";
import { reversePercent } from "../utils";

type IncomeType = "all" | "no" | "some";

type FormData = {
  incomeType?: IncomeType;
  rate?: number | null;
};

export const ModalUpdateWalletDepositRate: React.FC<
  WSModalCustomProps<void>
> = props => {
  const userId = useUserId();
  const queryPayoutSettings = usePayoutSettings(userId);

  const [mutate] = useWSMutation<
    any,
    any,
    {
      values: FormData;
    }
  >(
    async ({ values }) => {
      let walletDistibution: number;
      if (values.incomeType === "all") {
        walletDistibution = 100;
      } else if (values.incomeType === "no") {
        walletDistibution = 0;
      } else {
        walletDistibution = values.rate || 0;
      }

      await paymentsService.payoutSettings.updateWalletDistribution(
        userId,
        walletDistibution
      );
    },
    {
      dependencies: [QUERY_PAYOUT_SETTINGS],
      onSuccess: () => {
        props.onClose();
      }
    }
  );

  return (
    <WSModal
      title="How much of your income would you like deposited into your Wingspan Wallet account?"
      size="S"
      {...props}
    >
      <WSQueries
        queries={{
          queryPayoutSettings
        }}
      >
        {({ queryPayoutSettings }) => {
          const depositRatePercentage =
            queryPayoutSettings.data.payoutDestinations.find(p =>
              Number.isInteger(p.percentage)
            )?.percentage;
          const normalPercent = reversePercent(depositRatePercentage);

          return (
            <WSForm<FormData>
              onSubmit={async formData => {
                await mutate({ values: formData });
              }}
              defaultValues={{
                incomeType:
                  normalPercent === 100
                    ? "all"
                    : normalPercent === 0
                    ? "no"
                    : "some",
                rate:
                  normalPercent === 100
                    ? 30
                    : normalPercent === 0
                    ? 30
                    : normalPercent
              }}
              validationSchema={Yup.object().shape({
                rate: Yup.number().min(0).max(100).nullable()
              })}
            >
              <WSList gap="XL">
                <WSForm.Field
                  name="incomeType"
                  component={WSControlGroup}
                  componentProps={{
                    type: "radio",
                    chip: true,
                    options: [
                      {
                        label: "All income",
                        value: "all"
                      },
                      {
                        label: "No income",
                        value: "no"
                      },
                      {
                        label: "Some income",
                        value: "some"
                      }
                    ]
                  }}
                />

                <WSForm.Value name="incomeType">
                  {incomeType =>
                    incomeType === "some" ? (
                      <WSForm.Field
                        name="rate"
                        component={WSInputNumber}
                        label="Select income"
                        componentProps={{
                          size: "L",
                          iconRight: "percent",
                          align: "right",
                          min: 0,
                          max: 100
                        }}
                      />
                    ) : null
                  }
                </WSForm.Value>

                <WSList gap="S">
                  <WSActions
                    alignment="fill"
                    buttons={[
                      {
                        label: "Update deposit rate",
                        type: "submit",
                        kind: "Primary"
                      }
                    ]}
                  />
                  <WSText.ParagraphXs color="gray400">
                    Don’t worry, you can always change this again later.
                  </WSText.ParagraphXs>
                </WSList>
              </WSList>
            </WSForm>
          );
        }}
      </WSQueries>
    </WSModal>
  );
};

export const useModalUpdateWalletDepositRate = () =>
  useWSModalCustom(ModalUpdateWalletDepositRate);
